import React, { useEffect, useRef, useState } from "react";
import balanceImg from "@/assets/dashboard/balance_img.png";
import { useQuery } from "@tanstack/react-query";
import { ApiResponse } from "@/types/Api.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import {
  BalanceQueryKey,
  CurrencyQueryKey,
  ExchangeSettingsQueryKey,
  PendingBalanceQueryKey,
} from "@/config/constants/QueryKeys.tsx";
import { Balance } from "@/types/Balance.tsx";
import { Loader } from "@/components";
import { Currency } from "@/types/Currency";
import { AnimatePresence, motion } from "framer-motion";
import { isBitramp } from "@/utils/utils";
import balanceImgBitramp from "@/assets/SVG/balanceImg_bitramp.svg";
import { ExchangeSettings } from "@/types/Exchange";

const BalanceCard: React.FC = () => {
  const [selectedCurrency, setSelectedCurrency] = useState<string>("BTC");
  const [approvedValue, setApprovedValue] = useState<number>(0);
  const [pendingValue, setPendingValue] = useState<number>(0);
  const [openCurency, setOpenCurency] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: exchangeSettings } = useQuery<ApiResponse<ExchangeSettings>>({
    queryFn: async () =>
      GenericApi.get<ExchangeSettings>(ApiRoutes.EXCHANGESETTINGS),
    queryKey: [ExchangeSettingsQueryKey],
  });

  const { data: balance, isLoading: isLoadingBalance } = useQuery<
    ApiResponse<Balance[]>
  >({
    queryFn: async () => GenericApi.get<Balance[]>(ApiRoutes.GET_BALANCE),
    queryKey: [BalanceQueryKey],
  });

  const { data: pendingBalance, isLoading: isLoadingPendingBalance } = useQuery<
    ApiResponse<Balance[]>
  >({
    queryFn: async () =>
      GenericApi.get<Balance[]>(ApiRoutes.GET_BALANCE_PENDING),
    queryKey: [PendingBalanceQueryKey],
  });

  const { data: currencies, isLoading: isLoadingCurrencies } = useQuery<
    ApiResponse<Currency[]>
  >({
    queryFn: async () => GenericApi.get<Currency[]>(ApiRoutes.CURRENCY),
    queryKey: [CurrencyQueryKey],
  });

  console.log(currencies?.data?.map((currency) => currency.price));

  useEffect(() => {
    if (balance?.data) {
      const foundBalance = balance.data.find(
        (item) => item.currency === selectedCurrency
      );
      setApprovedValue(foundBalance ? Number(foundBalance.balance) : 0);
    }

    if (pendingBalance?.data) {
      const foundPendingBalance = pendingBalance.data.find(
        (item) => item.currency === selectedCurrency
      );
      setPendingValue(
        foundPendingBalance ? Number(foundPendingBalance.balance) : 0
      );
    }
  }, [selectedCurrency, balance, pendingBalance]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenCurency(false);
      }
    }

    if (openCurency) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openCurency]);

  if (isLoadingBalance || isLoadingPendingBalance || isLoadingCurrencies) {
    return <Loader />;
  }

  return (
    <div
      className="balance min-h-[10.75rem]"
      style={{
        backgroundImage: `url(${isBitramp() ? balanceImgBitramp : balanceImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        borderRadius: "0.625rem",
      }}
    >
      <div className="w-full px-8 py-6 min-h-[10.75rem] flex flex-col justify-between">
        <div className="flex justify-start h-full items-start gap-10 w-full">
          <p className="Heading2 white font-[300]">Balance</p>
          <div className="mt-3 ml-6">
            <p className="Subtitle medium text-[#0ac800]">Approved</p>
            <p className="Subtitle white bold flex items-center">
              {exchangeSettings?.data?.balance_enabled
                ? approvedValue
                : "0.0000"}{" "}
              {""}
              {/* <span>
                <select
                  className="text-[#ffffff] font-[300] text-[1.25rem] bg-transparent border-transparent focus:border-transparent"
                  value={selectedCurrency}
                  onChange={handleCurrencyChange}
                >
                  {currencies?.data?.map((currency) => (
                    <option
                      className="text-black"
                      key={currency.symbol}
                      value={currency.symbol}
                    >
                      {currency.symbol}
                    </option>
                  ))}
                </select>{" "}
              </span> */}
              <div className="relative py-2 border-none focus:ring-0  cursor-pointer w-fit ps-4 pe-8 semi-bold white Subtitle">
                <div
                  onClick={() => setOpenCurency(!openCurency)}
                  className="h-full flex justify-start items-center gap-2 cursor-pointer"
                >
                  {selectedCurrency}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="11"
                    height="10"
                    viewBox="0 0 11 10"
                    fill="none"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <path
                      d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                      fill="#737791"
                    />
                  </svg>
                </div>

                <AnimatePresence>
                  {openCurency && (
                    <motion.div
                      ref={dropdownRef}
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-1 left-[5rem] w-[10rem] GlassContainerc dark-blue pshadow z-10 h-[13rem] flex justify-center items-center"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className={`dark-blue w-[93%] z-10 h-[12rem] overflow-y-auto ${
                          isBitramp()
                            ? "crypto-list_bitramp"
                            : "crypto-list-gray"
                        }`}
                      >
                        {currencies?.data?.map((currency) => (
                          <div
                            key={currency.symbol}
                            onClick={() => {
                              setSelectedCurrency(currency?.symbol ?? "");
                              setOpenCurency(false);
                            }}
                            className="px-4 py-2 hover:bg-gray-400 cursor-pointer rounded-[0.625rem] flex justify-start align-center gap-2"
                          >
                            {currency.symbol}
                          </div>
                        ))}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </p>
            <p className="Subtitle light-blue">≈ WIP $</p>
          </div>
          <div className="flex-1 flex justify-between items-end flex-col h-[8rem]">
            <div>
              <p className="Subtitle medium white">Pending</p>
              <p className="Text light-blue medium">
                {exchangeSettings?.data?.balance_enabled ? pendingValue : "0"}{" "}
                <span className="light-blue">{selectedCurrency}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceCard;
